import React, { useState, useEffect } from 'react';
import {
  Flame,
  Sparkles,
  MapPin,
  Compass,
  Droplet,
  FlaskConical,
  Leaf,
  Sliders
} from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils';

const ICON_MAP = {
  'fire-burner': Flame,
  'kobberkedel': Flame,
  'hand-holding-seedling': Sparkles,
  'manuelt': Sparkles,
  'location-dot': MapPin,
  'lokation': MapPin,
  'compass': Compass,
  'kompas': Compass,
  'glass-water-droplet': Droplet,
  'renhed': Droplet,
  'flask-round-potion': FlaskConical,
  'raavarer': FlaskConical,
  'leaf': Leaf,
  'sankning': Leaf,
  'sliders': Sliders,
  'fleksibilitet': Sliders,
};

const EXPERIENCE_PILLARS = [
  {
    id: 'produktion',
    number: '01',
    keyword: 'Håndværk & Produktion',
    tagline: 'Kobberdestillation & Batch-præcision',
    body: 'Hver dråbe formes i hånden i vores klassiske kobberkedler på Sydals. Vi destillerer langsomt for at indfange de reneste æteriske olier og aromaer.',
    graphicTokens: [
      { icon: 'fire-burner', label: 'Kobberkedler', sub: 'Klassisk destillation' },
      { icon: 'hand-holding-seedling', label: '100% Manuelt', sub: 'Batch-præcision' }
    ],
    image: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/billede-5.png',
    video: null,
    watermark: 'HÅNDVÆRK'
  },
  {
    id: 'historie',
    number: '02',
    keyword: 'Historie & Rødder',
    tagline: 'Født på Sydals med udsyn til verden',
    body: 'Skabt i hjertet af det sydalsiske kystlandskab på Holtvej 8. Vores historie er drevet af passion for at sætte et uudsletteligt aftryk på dansk spirituskultur.',
    graphicTokens: [
      { icon: 'location-dot', label: 'Sydals · Holtvej 8', sub: 'Kystnært mikroklima' },
      { icon: 'compass', label: 'Lokal Identitet', sub: 'Dansk spirituskultur' }
    ],
    image: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/img-7021-650x867.jpg',
    video: null,
    watermark: 'HISTORIE'
  },
  {
    id: 'tilgang',
    number: '03',
    keyword: 'Tilgang & Kvalitet',
    tagline: 'Ufiltreret renhed og kompromisløs smag',
    body: 'Vi filtrerer ikke karakteren ud af vores flasker. Resultatet er en intens, silkeblød tekstur med en naturlig dybde, der taler direkte til sanserne.',
    graphicTokens: [
      { icon: 'glass-water-droplet', label: 'Ufiltreret Sjæl', sub: 'Bevarer æteriske olier' },
      { icon: 'flask-round-potion', label: 'Rene Råvarer', sub: 'Uden tilsætning' }
    ],
    image: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/kalas-pink-650x867.jpg',
    video: null,
    watermark: 'KVALITET'
  },
  {
    id: 'baeredygtighed',
    number: '04',
    keyword: 'Bæredygtighed & Fleksibilitet',
    tagline: 'Cirkulær omtanke og skræddersyede løsninger',
    body: 'Vi sanker vilde bær i lokalområdet, minimerer spild i produktionen og tilbyder fleksible aftapninger – fra signaturprodukter til skræddersyede partnerskabs-batches.',
    graphicTokens: [
      { icon: 'leaf', label: 'Cirkulær Sankning', sub: 'Vilde bær & nul spild' },
      { icon: 'sliders', label: 'Fleksibilitet', sub: 'Private label & special batches' }
    ],
    image: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/img-5469-800x1200.jpeg',
    video: null,
    watermark: 'CIRKULÆR'
  }
];

export default function Experience() {
  const [pillars, setPillars] = useState(EXPERIENCE_PILLARS);

  useEffect(() => {
    async function fetchPillars() {
      try {
        const res = await fetch('/_api/public/v2/content/oplevelsen?sort=sortering&order=asc');
        if (res.ok) {
          const json = await res.json();
          if (json?.items && json.items.length > 0) {
            const mapped = json.items.map((item, idx) => {
              const terms = item.terms?.['ikon-type'] || [];
              const rawImg = Array.isArray(item.billede) ? item.billede[0] : item.billede;
              return {
                id: item.id || `pillar-${idx}`,
                number: item.nummer || `0${idx + 1}`,
                keyword: item.overskrift || '',
                tagline: item.tagline || '',
                body: item.beskrivelse || '',
                watermark: item.vandmaerke || 'KALAS',
                image: rawImg,
                graphicTokens: [
                  {
                    icon: terms[0] || 'kobberkedel',
                    label: item.spec_1_titel || '',
                    sub: item.spec_1_beskrivelse || ''
                  },
                  {
                    icon: terms[1] || terms[0] || 'manuelt',
                    label: item.spec_2_titel || '',
                    sub: item.spec_2_beskrivelse || ''
                  }
                ]
              };
            });
            setPillars(mapped);
          }
        }
      } catch (err) {
        console.warn('Using fallback experience pillars:', err);
      }
    }
    fetchPillars();
  }, []);

  return (
    <section
      id="experience"
      className="relative w-full bg-[#F6F2EB] py-24 sm:py-32 md:py-40 px-4 sm:px-6 md:px-12 text-[#37332F] overflow-hidden"
      aria-label="The Experience - Om KALAS"
    >
      {/* Subtle Warm Background Glows */}
      <div className="absolute top-1/4 -right-40 w-96 h-96 bg-[#DED7CC]/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 -left-40 w-96 h-96 bg-[#C6D3D4]/25 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-7xl">
        {/* Section Header with Refined Graphic Typography */}
        <div className="mb-20 md:mb-28 border-b border-[#DED7CC] pb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="font-['Montserrat'] text-xs uppercase tracking-[0.3em] text-[#C99850] block mb-2">
              Universet · Historien · Håndværket
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-6xl md:text-7xl font-light uppercase tracking-[0.2em] text-[#37332F] leading-none break-words">
              The Experience
            </h2>
          </div>
          <p className="max-w-md text-xs sm:text-sm text-[#5E5852] font-light leading-relaxed">
            Hver dråbe er formet af det sydalsiske kystlandskab, traditionel kobberdestillation og kompromisløst råvarehåndværk.
          </p>
        </div>

        {/* 4 Graphic & Visual Storytelling Pillars in Editorial Layout */}
        <div className="space-y-28 sm:space-y-36 md:space-y-48">
          {pillars.map((pillar, index) => {
            const isEven = index % 2 === 0;

            return (
              <div
                key={pillar.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 items-center relative pt-8 sm:pt-12 w-full"
              >
                {/* Watermark Typographic Word */}
                <div
                  className={`absolute -top-7 sm:-top-11 md:-top-14 font-['Cormorant_Garamond'] text-3xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-[#37332F]/5 uppercase tracking-[0.25em] select-none pointer-events-none z-0 whitespace-nowrap overflow-hidden max-w-full ${
                    isEven ? 'left-0 text-left' : 'right-0 text-right'
                  }`}
                >
                  {pillar.watermark}
                </div>

                {/* Left/Right Text & Graphic Card */}
                <div
                  className={`lg:col-span-6 min-w-0 w-full relative z-10 space-y-6 ${
                    isEven ? 'lg:order-1' : 'lg:order-2'
                  }`}
                >
                  <div className="space-y-2">
                    <span className="font-['Montserrat'] text-xs uppercase tracking-[0.25em] text-[#C99850] font-medium block">
                      {pillar.number} · {pillar.tagline}
                    </span>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl font-light uppercase tracking-[0.15em] text-[#37332F] leading-tight break-words">
                      {pillar.keyword}
                    </h3>
                  </div>

                  {/* Body text */}
                  <p className="text-sm sm:text-base text-[#5E5852] font-light leading-relaxed">
                    {pillar.body}
                  </p>

                  {/* Clean Specification Tokens with Fine Lines */}
                  <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-3 pt-6 border-t border-[#DED7CC]">
                    {pillar.graphicTokens.map((token) => (
                      <div
                        key={token.label}
                        className="p-4 bg-[#F2EEE7] border border-[#DED7CC] flex flex-col justify-between space-y-1.5 min-w-0"
                      >
                        <div className="flex items-center gap-2 text-[#C99850] min-w-0">
                          {(() => {
                            const IconComponent = ICON_MAP[token.icon] || Sparkles;
                            return <IconComponent className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />;
                          })()}
                          <span className="text-[11px] font-['Montserrat'] uppercase tracking-wider text-[#37332F] font-medium truncate">
                            {token.label}
                          </span>
                        </div>
                        <span className="text-[11px] text-[#5E5852] font-light truncate">
                          {token.sub}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Large Editorial Visual Surface */}
                <div
                  className={`lg:col-span-6 min-w-0 w-full relative z-10 ${
                    isEven ? 'lg:order-2' : 'lg:order-1'
                  }`}
                >
                  <div className="relative">
                    {/* Subtle moodboard background offset block */}
                    <div
                      className={`absolute -inset-3 sm:-inset-4 ${
                        index % 4 === 0
                          ? 'bg-[#EAE5DD]/70'
                          : index % 4 === 1
                          ? 'bg-[#C6D3D4]/25'
                          : index % 4 === 2
                          ? 'bg-[#D9C2BF]/25'
                          : 'bg-[#DED7CC]/50'
                      } -z-10`}
                    />

                    <div className="relative w-full aspect-[16/11] sm:aspect-[16/11] overflow-hidden bg-[#EAE5DD] border border-[#DED7CC] group shadow-sm">
                      <img
                        src={getOptimizedImageUrl(pillar.image, 1200, 88)}
                        alt={pillar.keyword}
                        className="w-full h-full object-cover brightness-[0.95] contrast-[0.98] sepia-[0.04] group-hover:scale-102 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />

                      {/* Subtle Vignette Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#37332F]/40 via-transparent to-transparent pointer-events-none" />

                      {/* Tactile Corner Badge */}
                      <div className="absolute bottom-3 left-3 bg-[#F6F2EB]/95 px-3 py-1 border border-[#DED7CC] text-[10px] font-['Montserrat'] uppercase tracking-[0.2em] text-[#37332F] pointer-events-none">
                        Destilleriet Als
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
